import { createTheme, alpha, type PaletteMode } from "@mui/material/styles";

// Altelix Brand — accents that stay constant in both light/dark modes
const purple = {
  main: "#6c5ce7",
  light: "#a29bfe",
  dark: "#4834d4",
};

const teal = {
  main: "#00cec9",
  light: "#55efc4",
  dark: "#00b894",
};

// Theme tokens — these flip per mode and are exposed as CSS variables
// so the hundreds of inline `sx`/style usages across the app stay in sync.
type ThemeTokens = {
  bgBase: string;        // page background
  bgElevated: string;    // cards / sheets
  bgElevated2: string;   // drawer
  bgTooltip: string;
  textPrimary: string;
  textOnElevated: string; // strong text on elevated surfaces (was #fff in dark)
  textSecondary: string;
  textMuted: string;
  textFaint: string;
  textFaintest: string;
  overlay2: string;
  overlay4: string;
  overlay6: string;
  overlay8: string;
  overlay12: string;
  borderSoft: string;
  border: string;
  borderStrong: string;
  scrim: string;
  scrollbarThumb: string;
  // Vivid metric/accent text colors that must stay readable on the
  // current bg. Light mode uses darker variants so the text isn't washed out.
  metricWarning: string;
  metricInfo: string;
  metricSuccess: string;
  metricPrimary: string;
  metricSecondary: string;
  // Accent text used for clickable links / titles on elevated surfaces.
  linkAccent: string;
  brandTitleStart: string; // gradient start for "Squad Finance" title
  // CSS `filter` value applied to the Altelix logo. Adds a soft blue glow
  // in light mode so the navy logo lifts off white/light surfaces; "none" in dark.
  logoFilter: string;
};

const darkTokens: ThemeTokens = {
  bgBase:        "#080c16",
  bgElevated:    "#0e1525",
  bgElevated2:   "#0a0f1e",
  bgTooltip:     "#1a2035",
  textPrimary:   "#e0e4ef",
  textOnElevated:"#ffffff",
  textSecondary: "#8b90a0",
  textMuted:     "#5a5e6e",
  textFaint:     "#4a4e5e",
  textFaintest:  "#3a3e4e",
  overlay2:      "rgba(255,255,255,0.02)",
  overlay4:      "rgba(255,255,255,0.04)",
  overlay6:      "rgba(255,255,255,0.06)",
  overlay8:      "rgba(255,255,255,0.08)",
  overlay12:     "rgba(255,255,255,0.12)",
  borderSoft:    "rgba(255,255,255,0.04)",
  border:        "rgba(255,255,255,0.06)",
  borderStrong:  "rgba(255,255,255,0.10)",
  scrim:         "rgba(8,12,22,0.7)",
  scrollbarThumb:alpha(purple.main, 0.3),
  metricWarning:  "#fdcb6e",
  metricInfo:     "#74b9ff",
  metricSuccess:  "#55efc4",
  metricPrimary:  "#a29bfe",
  metricSecondary:"#00cec9",
  linkAccent:     "#a29bfe",
  brandTitleStart:"#ffffff",
  logoFilter:     "none",
};

// Light mode — carefully chosen so previously-light text never lands on a
// light background. Every secondary/muted/faint token stays >= 4.5:1
// contrast against bgBase (#f6f7fb) and bgElevated (#ffffff).
const lightTokens: ThemeTokens = {
  bgBase:        "#f5f7fb",
  bgElevated:    "#ffffff",
  bgElevated2:   "#ffffff",
  bgTooltip:     "#1f2937",
  textPrimary:   "#0f172a", // slate-900
  textOnElevated:"#0f172a",
  textSecondary: "#475569", // slate-600 — passes AA on white & on #f5f7fb
  textMuted:     "#64748b", // slate-500
  textFaint:     "#64748b", // keep readable — never go below slate-500 on light bg
  textFaintest:  "#64748b",
  overlay2:      "rgba(15,23,42,0.025)",
  overlay4:      "rgba(15,23,42,0.05)",
  overlay6:      "rgba(15,23,42,0.07)",
  overlay8:      "rgba(15,23,42,0.09)",
  overlay12:     "rgba(15,23,42,0.13)",
  borderSoft:    "rgba(15,23,42,0.06)",
  border:        "rgba(15,23,42,0.10)",
  borderStrong:  "rgba(15,23,42,0.14)",
  scrim:         "rgba(15,23,42,0.4)",
  scrollbarThumb:alpha(purple.main, 0.45),
  // Darker, AA-passing variants on white/light surfaces
  metricWarning:  "#a16207", // amber-700
  metricInfo:     "#1d4ed8", // blue-700
  metricSuccess:  "#047857", // emerald-700
  metricPrimary:  "#4338ca", // indigo-700
  metricSecondary:"#0e7490", // cyan-700
  linkAccent:     "#4338ca",
  brandTitleStart:"#0f172a",
  // Soft layered blue glow lifts the navy Altelix logo off light surfaces.
  logoFilter:
    "drop-shadow(0 1px 0 rgba(15,23,42,0.18)) " +
    "drop-shadow(0 4px 14px rgba(37,99,235,0.45)) " +
    "drop-shadow(0 0 22px rgba(37,99,235,0.30))",
};

function tokensToCssVars(t: ThemeTokens): Record<string, string> {
  return {
    "--c-bg-base":          t.bgBase,
    "--c-bg-elevated":      t.bgElevated,
    "--c-bg-elevated-2":    t.bgElevated2,
    "--c-bg-tooltip":       t.bgTooltip,
    "--c-text-primary":     t.textPrimary,
    "--c-text-on-elevated": t.textOnElevated,
    "--c-text-secondary":   t.textSecondary,
    "--c-text-muted":       t.textMuted,
    "--c-text-faint":       t.textFaint,
    "--c-text-faintest":    t.textFaintest,
    "--c-overlay-2":        t.overlay2,
    "--c-overlay-4":        t.overlay4,
    "--c-overlay-6":        t.overlay6,
    "--c-overlay-8":        t.overlay8,
    "--c-overlay-12":       t.overlay12,
    "--c-border-soft":      t.borderSoft,
    "--c-border":           t.border,
    "--c-border-strong":    t.borderStrong,
    "--c-scrim":            t.scrim,
    "--c-metric-warning":   t.metricWarning,
    "--c-metric-info":      t.metricInfo,
    "--c-metric-success":   t.metricSuccess,
    "--c-metric-primary":   t.metricPrimary,
    "--c-metric-secondary": t.metricSecondary,
    "--c-link-accent":      t.linkAccent,
    "--c-brand-title-start":t.brandTitleStart,
    "--c-logo-filter":      t.logoFilter,
  };
}

// Re-exported so non-component code (e.g. canvas/3D overlays) can read
// the active token values too.
export { darkTokens, lightTokens };

export function createAppTheme(mode: PaletteMode) {
  const t = mode === "light" ? lightTokens : darkTokens;
  const isLight = mode === "light";

  return createTheme({
    palette: {
      mode,
      primary: purple,
      secondary: teal,
      success: { main: "#00b894", light: "#55efc4", dark: "#00896a" },
      warning: { main: "#fdcb6e", light: "#ffeaa7", dark: "#e6a800" },
      error:   { main: "#ff7675", light: "#fab1a0", dark: "#d63031" },
      info:    { main: "#74b9ff", light: "#a1c4fd", dark: "#0984e3" },
      background: {
        default: t.bgBase,
        paper:   t.bgElevated,
      },
      text: {
        primary:   t.textPrimary,
        secondary: t.textSecondary,
        disabled:  t.textMuted,
      },
      divider: t.border,
      action: {
        hover:    alpha(purple.main, isLight ? 0.06 : 0.08),
        selected: alpha(purple.main, isLight ? 0.12 : 0.16),
        focus:    alpha(purple.main, isLight ? 0.10 : 0.12),
      },
    },
    typography: {
      fontFamily: "'IBM Plex Sans', 'Inter', system-ui, -apple-system, sans-serif",
      h4: { fontWeight: 700, letterSpacing: "-0.02em" },
      h5: { fontWeight: 700, letterSpacing: "-0.01em" },
      h6: { fontWeight: 600 },
      subtitle1: { fontWeight: 500 },
      subtitle2: { fontWeight: 600 },
      body2: { fontSize: "0.875rem", lineHeight: 1.6 },
      caption: { color: t.textSecondary },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ":root": tokensToCssVars(t),
          body: {
            background: t.bgBase,
            color: t.textPrimary,
            scrollbarWidth: "thin",
            scrollbarColor: `${t.scrollbarThumb} transparent`,
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              background: t.scrollbarThumb,
              borderRadius: 3,
            },
            "&::-webkit-scrollbar-track": { background: "transparent" },
          },
        },
      },
      MuiCard: {
        defaultProps: { variant: "outlined" },
        styleOverrides: {
          root: {
            background: isLight ? t.bgElevated : alpha("#0e1525", 0.8),
            backdropFilter: isLight ? "none" : "blur(20px)",
            border: `1px solid ${t.border}`,
            borderRadius: 16,
            transition: "border-color 0.2s ease, box-shadow 0.2s ease",
            "&:hover": {
              borderColor: alpha(purple.main, isLight ? 0.3 : 0.2),
              boxShadow: `0 4px 24px ${alpha(purple.main, isLight ? 0.10 : 0.08)}`,
            },
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: "20px 24px",
            "&:last-child": { paddingBottom: 20 },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 10,
            padding: "8px 20px",
            transition: "all 0.2s ease",
          },
          contained: {
            color: "#fff",
            background: `linear-gradient(135deg, ${purple.main}, ${purple.dark})`,
            boxShadow: `0 2px 12px ${alpha(purple.main, 0.3)}`,
            "&:hover": {
              background: `linear-gradient(135deg, ${purple.light}, ${purple.main})`,
              boxShadow: `0 4px 20px ${alpha(purple.main, 0.4)}`,
            },
          },
          containedSuccess: {
            color: "#fff",
            background: `linear-gradient(135deg, #00b894, #00896a)`,
            boxShadow: `0 2px 12px ${alpha("#00b894", 0.3)}`,
            "&:hover": {
              background: `linear-gradient(135deg, #55efc4, #00b894)`,
              boxShadow: `0 4px 20px ${alpha("#00b894", 0.4)}`,
            },
          },
          containedError: {
            color: "#fff",
            background: `linear-gradient(135deg, #ff7675, #d63031)`,
            boxShadow: `0 2px 12px ${alpha("#d63031", 0.3)}`,
            "&:hover": {
              background: `linear-gradient(135deg, #fab1a0, #ff7675)`,
            },
          },
          outlined: {
            borderColor: t.borderStrong,
            "&:hover": {
              borderColor: alpha(purple.main, 0.5),
              background: alpha(purple.main, 0.08),
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            fontSize: "0.75rem",
            borderRadius: 8,
          },
          outlined: {
            borderColor: t.borderStrong,
          },
          colorSuccess: {
            background: alpha("#00b894", isLight ? 0.12 : 0.15),
            color: isLight ? "#00684f" : "#55efc4",
            border: `1px solid ${alpha("#00b894", 0.3)}`,
          },
          colorWarning: {
            background: alpha("#fdcb6e", isLight ? 0.18 : 0.15),
            color: isLight ? "#7a5300" : "#ffeaa7",
            border: `1px solid ${alpha("#fdcb6e", 0.4)}`,
          },
          colorError: {
            background: alpha("#ff7675", isLight ? 0.12 : 0.15),
            color: isLight ? "#a01a1a" : "#fab1a0",
            border: `1px solid ${alpha("#ff7675", 0.3)}`,
          },
          colorPrimary: {
            background: alpha(purple.main, isLight ? 0.10 : 0.15),
            color: isLight ? purple.dark : purple.light,
            border: `1px solid ${alpha(purple.main, 0.3)}`,
          },
          colorInfo: {
            background: alpha("#74b9ff", isLight ? 0.12 : 0.15),
            color: isLight ? "#0a4f8a" : "#a1c4fd",
            border: `1px solid ${alpha("#74b9ff", 0.3)}`,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            "& .MuiTableCell-head": {
              background: alpha(purple.main, isLight ? 0.04 : 0.06),
              color: t.textSecondary,
              fontWeight: 700,
              fontSize: "0.7rem",
              textTransform: "uppercase",
              letterSpacing: "1.5px",
              borderBottom: `1px solid ${t.border}`,
              padding: "12px 16px",
            },
          },
        },
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            "& .MuiTableRow-root": {
              transition: "background 0.15s ease",
              "&:hover": { background: alpha(purple.main, 0.04) },
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${t.borderSoft}`,
            padding: "12px 16px",
            fontSize: "0.8125rem",
            color: t.textPrimary,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 10,
              "& fieldset": { borderColor: t.borderStrong },
              "&:hover fieldset": { borderColor: alpha(purple.main, 0.4) },
              "&.Mui-focused fieldset": { borderColor: purple.main },
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: t.borderStrong },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: alpha(purple.main, 0.4) },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            height: 6,
            backgroundColor: alpha(purple.main, 0.1),
          },
          bar: {
            borderRadius: 6,
            background: `linear-gradient(90deg, ${purple.main}, ${teal.main})`,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            background: `linear-gradient(90deg, ${purple.main}, ${teal.main})`,
            height: 3,
            borderRadius: "3px 3px 0 0",
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            "&.Mui-selected": { color: isLight ? purple.dark : purple.light },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            background: t.bgElevated,
            border: `1px solid ${t.border}`,
            borderRadius: 16,
            backdropFilter: isLight ? "none" : "blur(20px)",
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 10 },
          standardSuccess: {
            background: alpha("#00b894", 0.1),
            border: `1px solid ${alpha("#00b894", 0.2)}`,
          },
          standardWarning: {
            background: alpha("#fdcb6e", 0.1),
            border: `1px solid ${alpha("#fdcb6e", 0.2)}`,
          },
          standardError: {
            background: alpha("#ff7675", 0.1),
            border: `1px solid ${alpha("#ff7675", 0.2)}`,
          },
          standardInfo: {
            background: alpha("#74b9ff", 0.1),
            border: `1px solid ${alpha("#74b9ff", 0.2)}`,
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            "&.Mui-checked": {
              color: purple.main,
              "& + .MuiSwitch-track": {
                backgroundColor: alpha(purple.main, 0.5),
              },
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: t.bgTooltip,
            color: "#fff",
            border: `1px solid ${t.border}`,
            borderRadius: 8,
            fontSize: "0.75rem",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            background: t.bgElevated2,
            color: t.textPrimary,
            borderRight: `1px solid ${t.border}`,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: isLight ? "rgba(255,255,255,0.85)" : "rgba(8,12,22,0.8)",
            color: t.textPrimary,
            backdropFilter: "blur(20px)",
            borderBottom: `1px solid ${t.border}`,
            boxShadow: "none",
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            margin: "2px 8px",
            padding: "8px 16px",
            color: t.textPrimary,
            transition: "all 0.2s ease",
            "&:hover": {
              background: alpha(purple.main, 0.08),
            },
            "&.Mui-selected": {
              background: `linear-gradient(135deg, ${alpha(purple.main, 0.2)}, ${alpha(teal.main, 0.1)})`,
              borderLeft: `3px solid ${purple.main}`,
              "&:hover": {
                background: `linear-gradient(135deg, ${alpha(purple.main, 0.25)}, ${alpha(teal.main, 0.12)})`,
              },
              "& .MuiListItemIcon-root": { color: isLight ? purple.dark : purple.light },
              "& .MuiListItemText-primary": {
                color: isLight ? purple.dark : "#fff",
                fontWeight: 600,
              },
            },
          },
        },
      },
      MuiSnackbar: {
        defaultProps: {
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
        },
      },
    },
  });
}

// Backwards-compatible default export — defaults to LIGHT theme.
export const theme = createAppTheme("light");
